import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import some from 'lodash/some';
import omitBy from 'lodash/omitBy';
import range from 'lodash/range';

import {
  ChartVizType,
  ProjectFeatureFlag,
  PropertyType,
  ResourceType,
  SavedLegend,
  SHOW_LAST_X_DAYS_FEATURE_FLAGS,
  SHOW_LAST_X_DAYS_VALUES,
  ValueRepresentationType,
} from 'common/report/constants';
import {currentProjectStore} from 'common/report/stores/project-details';
import {FullLengthDateRange} from 'common/widgets/date-range-picker/models/full-length-date-range';
import {DateRangeType, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {ComparisonDate} from 'common/query-builder/query-block/time-comparison-menu/types';
import {
  comparisonDateFromBookmarkFormat,
  comparisonDateToBookmarkFormat,
} from 'common/query-builder/query-block/time-comparison-menu/utils';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {
  CohortSegment,
  getFirstSegment,
  isCohortSegment,
  isEventSegment,
  isOtherSegment,
  isUserSegment,
  OtherSegmentation,
  Segment,
} from 'common/query-builder/query-block/segment-menu/types';
import {FunnelAttribution} from 'common/query-builder/query-builder/types';
import {
  bookmarkFilterByCohortToCohortFilters,
  getFilterByCohorts,
} from 'common/query-builder/query-block/filter-menu/util';
import {
  isGroupedEntry,
  QueryEventEntry,
  QueryGroupedEntry,
  QuerySuperEntry,
} from 'common/query-builder/query-entry-section/types';
import {
  bookmarkGroupByToGroupBy,
  getGroupByProperties,
  groupByToBookmarkGroupBy,
  isGroupByEqual,
} from 'common/query-builder/query-block/segment-menu/util';
import {
  ChartType,
  ColorChoice,
  ConversionWindow,
  CountType,
  FunnelBookmark,
  FunnelsAggregateType,
  SelectedColumns,
  TrendChartType,
  TrendCountType,
  TrendUnit,
} from 'common/report/funnels/models/types';
import {
  mapBookmarkExclusionStepToEventEntry,
  mapBookmarkStepsToEntries,
  mapBookmarkToAttribution,
  mapBookmarkToHoldPropConstant,
  mapEntriesToBookmarkSteps,
  mapEventEntryToBookmarkExclusionStep,
  mapHoldPropConstantToBookmark,
} from 'common/report/funnels/models/format';
import {BucketDefinition, TimeBucketDefinition} from 'common/report/funnels/models/bar-chart/types';
import {getAlphaLabelFromIndex} from 'common/util';
import {
  DEFAULT_STEP_SORT_FIELD,
  fromEnum,
  getAllowedTrendUnits,
  getDefaultSortConfigForBookmark,
  getSegmentColumnSpecs,
  getTimeBucketsFromConversionWindow,
  isAnyOrderFunnel,
} from 'common/report/funnels/util';
import {FrequencyViewType} from 'common/report/funnels/models/frequency-chart/types';
import {ProfileHistoryType, Segmentation} from 'common/types/reports/query';
import {SortConfig} from 'common/report/util/sorting/types';
import {ChartType as CommonChartType} from 'common/report/constants';
import {LiftComparison} from 'common/widgets/lift-comparison-menu/types';
import {EventItem} from 'common/query-builder/query-block/events-menu/types';
import {LiftType} from 'common/report/util/lift/types';
import {
  getDefaultSortConfig,
  getPrimaryValueField,
  sanitizeSortConfig,
  updateSortingForNewNumSegments,
} from 'common/report/util/sorting/table-sorting';
import {GroupSortableStepColumns} from 'common/report/insights/charts/funnels-step-chart/table-util';
import {GroupSortableTrendFields} from 'common/components/data-table/trend-data/constants';
import {FUNNELS_TIME_TO_CONVERSION} from 'common/query-builder/query-block/properties-menu/constants';

import {
  ConversionWindowUnit,
  FREQUENCY_VIZ_TYPES,
  FunnelOrder,
  FunnelsCountType,
  STEPS_VIZ_TYPES,
  TableToggleableMetrics,
  TTC_DISTRIBUTION_VIZ_TYPES,
  TTC_MEDIAN_VIZ_TYPES,
} from './constants';

type Comparator<T = any> = (a: T, b: T, forQuery?: boolean) => boolean;

/** comparator that returns equivalent forQuery every time */
function notForQuery<T>(fallback: Comparator<T>): Comparator<T> {
  return function (a: T, b: T, forQuery?: boolean) {
    if (forQuery) {
      return true;
    }
    return fallback(a, b, forQuery);
  };
}

/** Utility to generate a comparison function using .equals */
function dotEquals<T extends {equals(o: T): boolean}>(a: T, b: T): boolean {
  return a.equals(b);
}

function tripleEquals(a: any, b: any) {
  return a === b;
}

function tripleEqualsIfFalseyElse<T>(fallback: Comparator<T>) {
  return function (a, b, forQuery) {
    if (!(a && b)) {
      return a === b;
    } else {
      return fallback(a, b, forQuery);
    }
  };
}

function listsEqual<T>(comparator: Comparator<T>) {
  return function compareLists(la: T[], lb: T[], forQuery?: boolean): boolean {
    return la.length === lb.length && la.every((_, index) => comparator(la[index], lb[index]), forQuery);
  };
}

const filtersEqual = listsEqual(dotEquals);

/**
 * Determine if two event entries are eqivalent
 * @param {boolean} forQuery If it results in a difference in the query & results.
 */
function eventsEqual(
  eventA: QueryEventEntry | QueryGroupedEntry,
  eventB: QueryEventEntry | QueryGroupedEntry,
  forQuery: boolean,
): boolean {
  if (!eventA || !eventB) {
    return false;
  }

  if (isGroupedEntry(eventA) || isGroupedEntry(eventB)) {
    if (!isGroupedEntry(eventA) || !isGroupedEntry(eventB)) {
      return false;
    }

    return (
      isEqual(omit(eventA, [`event`]), omit(eventB, [`event`])) &&
      listsEqual(queryEventEntryEqual)(eventA.event, eventB.event, forQuery)
    );
  } else {
    return queryEventEntryEqual(eventA, eventB, forQuery);
  }
}

function queryEventEntryEqual(eventA: QueryEventEntry, eventB: QueryEventEntry, forQuery: boolean): boolean {
  if (!eventA || !eventB) {
    return false;
  }

  // Note(SRFE-3326): 'custom' is an extra boolean field that is not necessary to determine the difference between
  // standard or custom events. It will be omitted for now to fix some erroneous situations that the field is not included
  // and set to false
  const omitFields = [
    `id`,
    `name`,
    `label`,
    `custom`,
    `isRecentlyViewed`,
    `isRecentlyUsed`,
    `tags`,
    `isCollectEverything`,
    ...(forQuery ? [`renamed`] : []),
  ];
  const [compareA, compareB] = [eventA, eventB].map((entry) => omit(entry.event, omitFields));

  return (
    // if under two filters, the operator doesn't matter
    (eventA.filters.length < 2 || eventA.filtersOperator === eventB.filtersOperator) &&
    Boolean(eventA.entryIndexLabelToggle) === Boolean(eventB.entryIndexLabelToggle) &&
    isEqual(compareA, compareB) &&
    filtersEqual(eventA.filters, eventB.filters)
  );
}

function exclusionStepsEqual(a: QueryEventEntry, b: QueryEventEntry, forQuery: boolean) {
  return (
    queryEventEntryEqual(a, b, forQuery) && isEqual(a.excludeRange, b.excludeRange) && a.excludeAll === b.excludeAll
  );
}

/** "Sanitizes" sorting (i.e. makes colSortAttrs.length equal number of segments), and potentially updates to
 * column sort (aka grouped sort) for multiseg or an equivalent flat sort (aka value sort) for single seg */
function updateSortingOnSegmentsChange({
  chartType,
  numSegmentColumns,
  oldNumSegmentColumns,
  sorting,
}: {
  chartType: ChartType;
  numSegmentColumns: number;
  oldNumSegmentColumns: number;
  sorting: SortConfig;
}): SortConfig {
  const valueField = getPrimaryValueField(sorting);
  const valueFieldIsGroupSortable =
    (chartType === ChartType.Chart && valueField in GroupSortableStepColumns) ||
    (chartType === ChartType.Trends && GroupSortableTrendFields.includes(valueField));

  return updateSortingForNewNumSegments({
    numSegmentColumns,
    oldNumSegmentColumns,
    valueFieldIsGroupSortable,
    sorting,
  });
}

/** Update sorting to be a column sort if possible without changing the sorting field */

const EQUIVALENCY_EVALUATORS: {[key: string]: Comparator} = Object.freeze({
  funnelAttributionSelection: isEqual,
  cohortFilters: filtersEqual,
  conversionWindow: isEqual,
  countType: tripleEquals,
  dataGroupId: tripleEquals,
  dateRange: tripleEqualsIfFalseyElse(dotEquals),
  comparedDateRange: tripleEqualsIfFalseyElse(isEqual),
  eventFilters: filtersEqual,
  otherFilters: filtersEqual,
  events: listsEqual(eventsEqual),
  exclusionSteps: listsEqual(exclusionStepsEqual),
  groupBy: isGroupByEqual,
  controlGroup: isEqual,
  holdingPropertyConstant: isEqual,
  userFilters: filtersEqual,
  timeBuckets: isEqual,
  frequencyBuckets: isEqual,
  trendUnit: tripleEquals,
  trendCountType: tripleEquals,
  funnelOrder: tripleEquals,
  chartType: tripleEquals,
  selectedTableColumns: notForQuery(isEqual),
  frequencyVizType: tripleEquals,
  ttcVizType: tripleEquals,
  stepsVizType: tripleEquals,
  liftComparison: isEqual,
  eventIndex: notForQuery(isEqual),
  savedLegend: notForQuery(isEqual),
  selected: notForQuery(isEqual),
  trendType: notForQuery(isEqual),
  frequencyType: notForQuery(isEqual),
  ttcValueRepresentation: notForQuery(isEqual),
  frequencyValueRepresentation: notForQuery(isEqual),
  sorting: notForQuery(isEqual),
  sortField: notForQuery(isEqual),
  sortOrder: notForQuery(isEqual),
});

const DEFAULT_CONV_WINDOW = {
  unit: ConversionWindowUnit.Day,
  value: 30,
};

const SELECTED_TABLE_COLUMN_OPTIONS: SelectedColumns = {
  [TableToggleableMetrics.StatSig]: true,
  [TableToggleableMetrics.ConvPrevStep]: true,
  [TableToggleableMetrics.ConvFirstStep]: false,
  [TableToggleableMetrics.Count]: true,
  [TableToggleableMetrics.TimePrevStep]: true,
  [TableToggleableMetrics.TimeFirstStep]: false,
};

// possibly move this to constants file
export const FUNNELS_DEFAULT_EVENT = {
  filters: [],
  event: {
    label: ``,
    value: null,
  },
  type: ResourceType.Event,
} as QueryEventEntry;

const FUNNELS_REPORT_DEFAULTS: Partial<FunnelsQuery> = Object.freeze({
  chartType: ChartType.Chart,
  countType: CountType.Unique,
  conversionWindow: DEFAULT_CONV_WINDOW,
  dataGroupId: null,
  dateRange: new FullLengthDateRange().getAttributes(),
  comparedDateRange: null,
  cohortFilters: [],
  eventFilters: [],
  otherFilters: [],
  events: [FUNNELS_DEFAULT_EVENT, FUNNELS_DEFAULT_EVENT],
  exclusionSteps: [],
  groupBy: [],
  holdingPropertyConstant: [],
  timeBuckets: getTimeBucketsFromConversionWindow(DEFAULT_CONV_WINDOW),
  frequencyBuckets: {min: 1, max: 20, bucketSize: 1, offset: 0},
  trendUnit: TrendUnit.Day,
  trendCountType: TrendCountType.Linear,
  funnelOrder: FunnelOrder.Loose,
  userFilters: [],
  controlGroup: [],
  funnelAttributionSelection: {action_mode: `last_touch`} as FunnelAttribution,
  selectedTableColumns: SELECTED_TABLE_COLUMN_OPTIONS,

  frequencyVizType: ChartVizType.Bar,
  ttcVizType: ChartVizType.Bar,
  stepsVizType: ChartVizType.Steps,
  frequencyValueRepresentation: ValueRepresentationType.Relative,
  ttcValueRepresentation: ValueRepresentationType.Relative,

  ttcAggregateType: FunnelsAggregateType.Distribution,
  frequencyAggregateType: FunnelsAggregateType.Distribution,
  liftComparison: null,

  eventIndex: null,
  savedLegend: [],
  // null fields are persisted to bookmark. These deprecated sorting fields are set to undefined to prevent them being written to
  // the bookmark unless explicitly set.
  sortField: undefined,
  sortOrder: undefined,
  sorting: getDefaultSortConfig({
    numSegmentColumns: 1,
    valueSortField: DEFAULT_STEP_SORT_FIELD,
  }),
  trendType: TrendChartType.Conversion,
  frequencyType: FrequencyViewType.Conversion,
  projectId: -1,
  version: 2,
});

export class FunnelsQuery {
  /**
   * Convert from bookmark to funnel query, reverse of "generateBookmark"
   * @param params {Object} bookmark params
   * @param projectId {projectId} necessary for arb-selector conversion of property filters
   */
  // TODO opslead
  public static fromBookmark(params: FunnelBookmark, projectId = FUNNELS_REPORT_DEFAULTS.projectId) {
    const attrs: Partial<FunnelsQuery> = {projectId};

    if (params.steps) {
      attrs.events = mapBookmarkStepsToEntries(params.steps);
    }
    if (params.exclusions) {
      attrs.exclusionSteps = params.exclusions.map(
        mapBookmarkExclusionStepToEventEntry.bind(null, attrs.events.length),
      );
    }
    if (params.aggregate_by) {
      attrs.holdingPropertyConstant = mapBookmarkToHoldPropConstant(params.aggregate_by);
    }
    if (params.length && params.length_unit) {
      attrs.conversionWindow = {
        value: parseInt(params.length as unknown as string, 10),
        unit: params.length_unit,
      };
      // TODO (dtwoodall): Old default date range logic, we should update this with something smarter or remove it entirely
      let dateWindow = 30;
      if (attrs.conversionWindow.value > 30 && attrs.conversionWindow.unit === `day`) {
        dateWindow = 90;
      }
      attrs.dateRange = new FullLengthDateRange({
        type: DateRangeType.RelativeAfter,
        window: {
          unit: TimeUnit.Day,
          value: dateWindow,
        },
      }).getAttributes();
    }
    if (params.time_buckets) {
      attrs.timeBuckets = {
        min: params.time_buckets.min,
        max: params.time_buckets.max,
        bucketSize: params.time_buckets.bucket_size,
        isDrilledDown: params.time_buckets.is_drilled_down,
        offset: params.time_buckets.offset,
      };
    }
    if (params.frequency_buckets) {
      attrs.frequencyBuckets = {
        min: params.frequency_buckets.min,
        max: params.frequency_buckets.max,
        bucketSize: params.frequency_buckets.bucket_size,
        offset: params.frequency_buckets.offset,
      };
    }
    if (params.group_by) {
      attrs.groupBy = bookmarkGroupByToGroupBy(params.group_by);
    }

    // TODO: Split this into cohortFilter and userFilters when we switch over to the new API
    attrs.cohortFilters = bookmarkFilterByCohortToCohortFilters(params.filter_by_cohort);

    if (params.date_range) {
      attrs.dateRange = FullLengthDateRange.fromBookmarkFormat(params.date_range).getAttributes();
    }
    if (params.time_comparison) {
      attrs.comparedDateRange = params.time_comparison
        ? comparisonDateFromBookmarkFormat(params.time_comparison)
        : null;
    }
    if (params.filter_by_event) {
      const getFilters = (filterType) => {
        return params.filter_by_event.children.map((child) => child[filterType]).filter(Boolean);
      };
      attrs.eventFilters = getFilters(`event`);
      attrs.userFilters = getFilters(`user`);
      attrs.otherFilters = getFilters(`other`);
    }
    if (params.count_type) {
      attrs.countType = fromEnum(params.count_type, CountType, CountType.Unique);
    }
    if (params.data_group_id) {
      attrs.dataGroupId = params.data_group_id;
    }
    if (params.trend_unit) {
      attrs.trendUnit = params.trend_unit;
    }
    if (params.trend_count_type) {
      attrs.trendCountType = params.trend_count_type;
    }
    if (params.chart_type) {
      attrs.chartType = fromEnum(params.chart_type, ChartType, ChartType.Chart);
    }

    if (params.funnel_order) {
      attrs.funnelOrder = fromEnum(params.funnel_order, FunnelOrder, FunnelOrder.Loose);
    }

    if (params.action_mode) {
      attrs.funnelAttributionSelection = mapBookmarkToAttribution(params.action_mode, params.action_step);
    }

    if (params.selectedTableColumns) {
      attrs.selectedTableColumns = params.selectedTableColumns;
    }

    if (params.stat_sig_control) {
      attrs.controlGroup = params.stat_sig_control;
    }

    let setStepAnyOrder = false;
    if (params.is_step_any_order && params.is_step_any_order.length === attrs.events.length) {
      attrs.events = attrs.events.map((event, idx) => ({
        ...event,
        entryIndexLabelToggle: params.is_step_any_order[idx],
      }));
      setStepAnyOrder = true;
    }

    // Support older bookmarks that do not store is_step_any_order information
    if (attrs.funnelOrder === FunnelOrder.Any && !setStepAnyOrder) {
      attrs.events.map((event) => (event.entryIndexLabelToggle = true));
      attrs.events[0].entryIndexLabelToggle = false;
    }

    // apply aggregate types
    if (params.ttcAggregateType) {
      attrs.ttcAggregateType = params.ttcAggregateType;
    }

    if (params.frequencyAggregateType) {
      attrs.frequencyAggregateType = params.frequencyAggregateType;
    }

    // apply lift params
    if (params.liftComparison) {
      attrs.liftComparison = params.liftComparison;
    }

    // Validate and apply viz types
    attrs.stepsVizType = STEPS_VIZ_TYPES.includes(params.stepsVizType) ? params.stepsVizType : ChartVizType.Steps;

    let ttcVizTypes = TTC_DISTRIBUTION_VIZ_TYPES;
    if (params.ttcAggregateType === FunnelsAggregateType.Median) {
      ttcVizTypes = TTC_MEDIAN_VIZ_TYPES;
    }
    attrs.ttcVizType = ttcVizTypes.includes(params.ttcVizType) ? params.ttcVizType : ChartVizType.Bar;

    attrs.frequencyVizType = FREQUENCY_VIZ_TYPES.includes(params.frequencyVizType)
      ? params.frequencyVizType
      : ChartVizType.Bar;

    if (params.frequencyValueRepresentation) {
      attrs.frequencyValueRepresentation = params.frequencyValueRepresentation;
    }

    if (params.ttcValueRepresentation) {
      attrs.ttcValueRepresentation = params.ttcValueRepresentation;
    }

    if (params.trendType) {
      attrs.trendType = params.trendType;
    }

    if (params.frequencyType) {
      attrs.frequencyType = params.frequencyType;
    }

    attrs.selected = params.selectedSegments;
    attrs.eventIndex = params.eventIndex;

    attrs.savedLegend = params.savedLegend;
    attrs.sortField = params.sortField;
    attrs.sortOrder = params.sortOrder;

    if (!params.sorting) {
      attrs.sorting = getDefaultSortConfigForBookmark(params);
    } else {
      const numSegmentColumns = getSegmentColumnSpecs(params).length || 1;
      attrs.sorting = sanitizeSortConfig({sorting: params.sorting, numSegmentColumns});
    }

    return new FunnelsQuery(attrs);
  }

  public static createFromUpdate(
    oldQuery: FunnelsQuery,
    newParams: Partial<FunnelsQuery>,
    {
      erfTreatments,
    }: {
      erfTreatments?: CohortSegment[];
    } = {},
  ) {
    if (erfTreatments) {
      newParams.groupBy = [...(newParams.groupBy ?? oldQuery.groupBy ?? []), erfTreatments];
    }

    const groupByChanged = `groupBy` in newParams && oldQuery.groupBy !== newParams.groupBy;
    const currentLiftNeedsSegments =
      oldQuery.liftComparison?.type === LiftType.Segment || oldQuery.liftComparison?.type === LiftType.Overall;
    if (groupByChanged && currentLiftNeedsSegments && !newParams.liftComparison) {
      newParams.liftComparison = null;
    }

    const breakdownMaybeChanged = groupByChanged || `events` in newParams;
    if (breakdownMaybeChanged) {
      const sorting = newParams.sorting || oldQuery.sorting;
      if (sorting) {
        const numSegmentColumns = _getSegmentNames({
          events: newParams.events || oldQuery.events,
          groupBy: newParams.groupBy || oldQuery.groupBy,
        }).length;
        const oldNumSegmentColumns = _getSegmentNames({events: oldQuery.events, groupBy: oldQuery.groupBy}).length;
        const chartType = newParams.chartType || oldQuery.chartType;
        newParams.sorting = updateSortingOnSegmentsChange({
          chartType,
          numSegmentColumns,
          oldNumSegmentColumns,
          sorting,
        });
      }
    }

    return new FunnelsQuery({...oldQuery, ...newParams});
  }

  public version: number;
  public countType: CountType;
  public funnelAttributionSelection: FunnelAttribution;
  public conversionWindow: ConversionWindow;
  public timeBuckets: TimeBucketDefinition;
  public frequencyBuckets: BucketDefinition;
  public dataGroupId: string;
  public dateRange: Partial<FullLengthDateRange>;
  public comparedDateRange: ComparisonDate;
  public cohortFilters: PropertyFilter[];
  public eventFilters: PropertyFilter[];
  public otherFilters: PropertyFilter[];
  public events: Array<QueryEventEntry | QueryGroupedEntry>;
  public exclusionSteps: QueryEventEntry[];
  public groupBy: Segment[];
  public holdingPropertyConstant: Segmentation[];
  // TODO: Switch type from TrendUnit to standardized TimeUnit
  public trendUnit: TrendUnit;
  public trendCountType: TrendCountType;
  public funnelOrder: FunnelOrder;
  public controlGroup: string[];
  public selectedTableColumns: SelectedColumns;
  public querySampling: boolean;

  public frequencyVizType: ChartVizType;
  public stepsVizType: ChartVizType;
  public ttcVizType: ChartVizType;

  public ttcAggregateType: FunnelsAggregateType;
  public frequencyAggregateType: FunnelsAggregateType;

  public liftComparison: LiftComparison;

  public userFilters: PropertyFilter[];
  public chartType: ChartType;

  public eventIndex: number;
  public savedLegend: SavedLegend[];
  public selected: ColorChoice[];
  public trendType: TrendChartType;
  public frequencyType: FrequencyViewType;
  public ttcValueRepresentation: ValueRepresentationType;
  public frequencyValueRepresentation: ValueRepresentationType;
  public sorting: SortConfig;
  public sortField: string;
  public sortOrder: number;

  // Global config that has leaked through to this class for serialization purposes.
  public projectId: number;

  /**
   * @param [attrs] - Attributes to set on the new FunnelsQuery object
   */
  // TODO opslead
  constructor(attrs: Partial<FunnelsQuery> = {}) {
    if (`conversionWindow` in attrs && (!attrs.timeBuckets || !attrs.timeBuckets.isDrilledDown)) {
      // replace the time buckets if conversion window is being updated. (except when drilled down)
      attrs.timeBuckets = getTimeBucketsFromConversionWindow(attrs.conversionWindow);
    }

    if (`funnelOrder` in attrs) {
      if (attrs.funnelOrder === FunnelOrder.Any) {
        if (!some(attrs.events.map((entry) => entry.entryIndexLabelToggle))) {
          attrs.funnelOrder = FunnelOrder.Loose;
        }
        if (attrs.groupBy && attrs.groupBy.length > 0) {
          const idx = attrs.groupBy.findIndex(isOtherSegment);
          if (idx >= 0) {
            attrs.groupBy.push(attrs.groupBy.splice(idx, 1)[0]);
          }
        }
      }
      if (attrs.funnelOrder === FunnelOrder.Loose) {
        if (attrs.groupBy) {
          attrs.groupBy = attrs.groupBy.filter(
            (grouping: Segment) =>
              Array.isArray(grouping) ||
              !(
                ResourceType.Other in grouping &&
                grouping[ResourceType.Other].propertyName !== FUNNELS_TIME_TO_CONVERSION.value
              ),
          );
        }
        if (attrs.chartType === ChartType.Sankey) {
          attrs.chartType = ChartType.Chart;
        }
        attrs.events = attrs.events.map((entry) => ({...entry, entryIndexLabelToggle: false}));
      }
    } else if (attrs.chartType === ChartType.Sankey) {
      attrs.funnelOrder = FunnelOrder.Any;
      attrs.events = _getDefaultAnyOrderSteps(attrs.events);
    }

    let funnelsDefaults = {...FUNNELS_REPORT_DEFAULTS};
    // Dynamic defaults

    const showLastXDaysFlag = SHOW_LAST_X_DAYS_FEATURE_FLAGS.filter((flag: ProjectFeatureFlag) =>
      currentProjectStore.value?.hasFeatureFlag(flag),
    );

    if (showLastXDaysFlag) {
      const value = SHOW_LAST_X_DAYS_VALUES[showLastXDaysFlag[0]];
      if (value) {
        const conversionWindow = {
          unit: ConversionWindowUnit.Day,
          value,
        };

        funnelsDefaults = {
          ...funnelsDefaults,
          dateRange: new FullLengthDateRange({
            type: DateRangeType.RelativeAfter,
            window: {
              unit: TimeUnit.Day,
              value,
            },
          }).getAttributes(),
          conversionWindow,
          timeBuckets: getTimeBucketsFromConversionWindow(conversionWindow),
        };
      }
    }

    const defaultFunnelsMath = currentProjectStore.value?.hasFeatureFlag(ProjectFeatureFlag.FunnelsMathSessions)
      ? ProjectFeatureFlag.FunnelsMathSessions
      : null;
    if (defaultFunnelsMath) {
      funnelsDefaults = {
        ...funnelsDefaults,
        countType: FunnelsCountType.Session,
      };
    }

    Object.assign(
      this,
      funnelsDefaults,
      omitBy(attrs, function omitEmptyStrings(value) {
        return value === ``;
      }),
    );

    // Instantiate models from primitive attrs
    this.dateRange = new FullLengthDateRange(this.dateRange);
    const arr = getAllowedTrendUnits(this.dateRange.getIntervalDays());
    if (!arr.includes(this.trendUnit)) {
      this.trendUnit = arr.includes(TrendUnit.Day) ? TrendUnit.Day : arr[0];
    }
    this.events = this.events.map((entry) => {
      if (isGroupedEntry(entry)) {
        entry.event = entry.event.map((nestedEntry) => ({
          ...nestedEntry,
          filters: constructPropertyFilters(nestedEntry.filters),
        }));
      }
      return {...entry, filters: constructPropertyFilters(entry.filters)};
    });
    this.exclusionSteps = this.exclusionSteps.map((entry) => ({
      ...entry,
      filters: constructPropertyFilters(entry.filters),
    }));

    // force filter initialization
    // eslint-disable-next-line no-self-assign
    this.filters = this.filters;
  }

  get filters(): PropertyFilter[] {
    return [...this.eventFilters, ...this.userFilters, ...this.cohortFilters, ...this.otherFilters];
  }

  set filters(filters) {
    this.eventFilters = constructPropertyFilters(filters.filter((item) => item.resourceType === ResourceType.Event));
    this.userFilters = constructPropertyFilters(filters.filter((item) => item.resourceType === ResourceType.User));
    this.cohortFilters = constructPropertyFilters(filters.filter((item) => item.resourceType === ResourceType.Cohort));
    this.otherFilters = constructPropertyFilters(filters.filter((item) => item.resourceType === ResourceType.Other));
  }

  /**
   * Determine if this funnels query is equal to another funnels query
   * @param {FunnelsQuery} otherQuery The other query to compare this query to
   * @returns {boolean}
   */
  public equals(otherQuery: FunnelsQuery) {
    // Assign to a temporary variable for easy debugging.
    const notEqual = Object.entries(EQUIVALENCY_EVALUATORS).filter(([attr, fn]) => {
      return !fn(this[attr], otherQuery[attr], false);
    });

    return notEqual.length === 0;
  }

  /**
   * Determine if this funnels query is equal to another funnels query in terms of requiring a re-query.
   * (i.e. will it generate a query that will return different results)
   * @param {FunnelsQuery} otherQuery The other query to compare this query to
   * @returns {boolean}
   */
  public generatedQueryEquals(otherQuery: FunnelsQuery) {
    // Assign to a temporary variable for easy debugging.
    const notEqual = Object.entries(EQUIVALENCY_EVALUATORS).filter(([attr, fn]) => {
      return !fn(this[attr], otherQuery[attr], true);
    });

    return notEqual.length === 0;
  }

  public hasChanges() {
    return !this.equals(new FunnelsQuery());
  }

  /** generate a bookmark object that can make a query and be persisted as a saved report */
  // TODO opslead
  public generateBookmark(): FunnelBookmark {
    const aggregateBy = mapHoldPropConstantToBookmark(this.holdingPropertyConstant);
    const steps = mapEntriesToBookmarkSteps(this.events);
    const exclusions = this.exclusionSteps.map((step) =>
      mapEventEntryToBookmarkExclusionStep(this.events.length, step),
    );
    const groupBy = groupByToBookmarkGroupBy(this.groupBy);
    const arr = getAllowedTrendUnits(this.dateRange.getIntervalDays());
    const comparedDateRange = this.comparedDateRange ? comparisonDateToBookmarkFormat(this.comparedDateRange) : null;

    if (!arr.includes(this.trendUnit)) {
      this.trendUnit = arr[0];
    }
    const params: FunnelBookmark = {
      ...(aggregateBy.length ? {aggregate_by: aggregateBy} : {}),
      ...(this.dataGroupId ? {data_group_id: this.dataGroupId} : {}),
      ...(this.controlGroup ? {stat_sig_control: this.controlGroup} : {}),
      chart_type: this.chartType,
      count_type: this.countType,
      date_range: this.dateRange.getBookmarkFormat(),
      time_comparison: comparedDateRange,
      exclusions,
      filter_by_cohort: JSON.stringify(this.cohortFilters.length ? getFilterByCohorts(this.cohortFilters) : {}),
      length: this.conversionWindow.value,
      length_unit: this.conversionWindow.unit,
      time_buckets: {
        min: this.timeBuckets.min,
        max: this.timeBuckets.max,
        offset: this.timeBuckets.offset,
        bucket_size: this.timeBuckets.bucketSize,
        is_drilled_down: this.timeBuckets.isDrilledDown,
      },
      frequency_buckets: {
        min: this.frequencyBuckets.min,
        max: this.frequencyBuckets.max,
        offset: this.frequencyBuckets.offset,
        bucket_size: this.frequencyBuckets.bucketSize,
      },
      trend_unit: this.trendUnit,
      trend_count_type: this.trendCountType,
      funnel_order: this.funnelOrder,
      version: this.version,
      steps,
      savedLegend: this.savedLegend,
      selectedTableColumns: this.selectedTableColumns,
      ttcVizType: this.ttcVizType,
      frequencyVizType: this.frequencyVizType,
      stepsVizType: this.stepsVizType,
      ttcAggregateType: this.ttcAggregateType,
      frequencyAggregateType: this.frequencyAggregateType,
      liftComparison: this.liftComparison,
      eventIndex: this.eventIndex,
      trendType: this.trendType,
      frequencyType: this.frequencyType,
      ttcValueRepresentation: this.ttcValueRepresentation,
      frequencyValueRepresentation: this.frequencyValueRepresentation,
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      sorting: this.sorting,
      selectedSegments: this.selected,
    };

    // action mode and action steps are set if eventFilters or eventGroupBys exist
    const eventFiltersExists: boolean = this.eventFilters && this.eventFilters.length > 0;
    const historicalProfileFiltersExists: boolean =
      this.userFilters?.length &&
      this.userFilters.some((item) => item.profileHistoryOptions?.type === ProfileHistoryType.EventTime);

    const eventGroupByExists: boolean = groupBy && groupBy.length > 0 && groupBy.some((item) => isEventSegment(item));
    const historicalProfileGroupByExists: boolean =
      groupBy?.length > 0 &&
      groupBy.some(
        (item) =>
          isUserSegment(item) && item[ResourceType.User].profileHistoryOptions?.type === ProfileHistoryType.EventTime,
      );

    const attributionSupported =
      eventFiltersExists || historicalProfileFiltersExists || eventGroupByExists || historicalProfileGroupByExists;
    if (this.funnelAttributionSelection && attributionSupported) {
      params.action_mode = this.funnelAttributionSelection.action_mode;
      if (this.funnelAttributionSelection.action_mode === `step`) {
        params.action_step = this.funnelAttributionSelection.action_step;
      }
    }

    if (this.isAnyOrderFunnel()) {
      params.is_step_any_order = this.events.map((entry) => entry.entryIndexLabelToggle || false);
    }

    if (!isEmpty(groupBy)) {
      params.group_by = groupBy;
    }

    // TODO: Remove userFilters length check once we switch to the new API
    if (this.eventFilters.length > 0 || this.userFilters.length > 0 || this.otherFilters.length > 0) {
      params.filter_by_event = this.getFilterByEvent();
    }

    return params;
  }

  public getFilterByEvent() {
    // TODO: Remove userFilter merge from this once we switch over to the new API
    return {
      operator: `and`,
      children: [
        ...this.eventFilters.map((filter) => ({event: filter})),
        ...this.userFilters.map((filter) => ({user: filter})),
        ...this.otherFilters.map((filter) => ({other: filter})),
      ],
    };
  }

  public isAnyOrderFunnel() {
    return isAnyOrderFunnel(this.funnelOrder);
  }

  public isSegmentedByTopPaths() {
    return this.groupBy && this.groupBy.length > 0 && this.groupBy.findIndex(isOtherSegment) >= 0;
  }

  public isGroupedSteps(): boolean {
    return this.events.some((event) => isGroupedEntry(event));
  }

  public shouldShowStatSig() {
    return !this.isAnyOrderFunnel() && !this.isGroupedSteps();
  }

  public isSegmented(): boolean {
    return this.groupBy.length > 0;
  }

  // TODO(@Gabe): iron/common/report/funnels/util.ts WidgetType is essentially this. Consolidate to what makes more sense
  public getCommonChartType(): CommonChartType {
    switch (this.chartType) {
      case ChartType.Chart:
        return {
          [ChartVizType.Steps]: CommonChartType.FunnelSteps,
          [ChartVizType.Metric]: CommonChartType.FunnelStepsMetric,
        }[this.stepsVizType];
      case ChartType.Trends:
        return CommonChartType.FunnelTrend;
      case ChartType.TimeToConvert:
        return {
          [ChartVizType.Line]: CommonChartType.FunnelTTCLine,
          [ChartVizType.Bar]: CommonChartType.FunnelTTCBar,
          [ChartVizType.Metric]: CommonChartType.FunnelMedianTTC,
        }[this.ttcVizType];
      case ChartType.Frequency:
        return {
          [ChartVizType.Line]: CommonChartType.FunnelFrequencyLine,
          [ChartVizType.Bar]: CommonChartType.FunnelFrequencyBar,
        }[this.frequencyVizType];
      case ChartType.Sankey:
        return CommonChartType.FlowsTopPaths;
      default:
        throw new Error(`Unhandled Chart Type`);
    }
  }

  /**
   * A funnel is considered valid from here if it has two usable events
   * Since we have placeholder events, they have to be filtered out.
   * Note: having two events is only usable to check validity from a FunnelsQuery object
   * since it fills in other default values such as daterange and charttype
   */
  public isValidForQuery() {
    return this.events.filter((entry) => (entry.event as EventItem)?.value || isGroupedEntry(entry)).length > 1;
  }

  /** Get segment names from the group by and events that are OR-steps. */
  getSegmentNames(): Array<string | string[]> {
    return _getSegmentNames({events: this.events, groupBy: this.groupBy});
  }

  public getSegmentTypes() {
    const r = [];
    this.events.forEach((el) => {
      if (isGroupedEntry(el)) {
        r.push(PropertyType.String);
      }
    });

    const groupByProperties = getGroupByProperties(this.groupBy);

    // Assume types are consistent across value comparisons
    return r.concat(groupByProperties.map((groupByProperty) => [].concat(groupByProperty)[0].propertyType));
  }

  public getStepLabels(ignoreAnyOrder = false) {
    if (!ignoreAnyOrder && this.isAnyOrderFunnel()) {
      const labels = Array(this.events.length);
      for (let i = 0; i < this.events.length; i++) {
        labels[i] = `Step ${i + 1}`;
      }
      return labels;
    } else {
      return this.events.map((step, index) => {
        const {event} = step;
        if (Array.isArray(event)) {
          return range(0, event.length)
            .map((idx) => `${index + 1}${getAlphaLabelFromIndex(idx)}`)
            .join(` or `);
        } else {
          return event.renamed || event.label;
        }
      });
    }
  }

  public getDefaultAnyOrderSteps() {
    return _getDefaultAnyOrderSteps(this.events);
  }
}

function _getDefaultAnyOrderSteps(fromEvents: FunnelsQuery[`events`]) {
  const events = fromEvents.map((entry) => ({...entry, entryIndexLabelToggle: true}));
  if (events.length > 0) {
    events[0].entryIndexLabelToggle = false;
  }
  return events;
}

/** convert property filter attributes into PropertyFilter instances */
function constructPropertyFilters(filterPrimitives: QuerySuperEntry['filters']) {
  return filterPrimitives.map((filter) => new PropertyFilter(filter));
}

/** Get segment names from the group by. If events are passed, OR-steps will be prepended to the list. */
function _getSegmentNames({events, groupBy}: Pick<FunnelsQuery, 'events' | 'groupBy'>) {
  const r = [];
  events.forEach((el, idx) => {
    if (isGroupedEntry(el)) {
      r.push(`Step ` + (idx + 1) + ` comparison`);
    }
  });

  return r.concat(
    (groupBy || []).map((segment) => {
      let segmentName;

      // In value comparison, grab the first segment in an array
      if (Array.isArray(segment) && !isCohortSegment(segment)) {
        segment = segment[0];
      }

      if (Array.isArray(segment)) {
        segmentName = `Cohort`;
      } else if (isEventSegment(segment) || isUserSegment(segment)) {
        return getFirstSegment(segment).propertyName;
      } else if (isOtherSegment(segment)) {
        return (getFirstSegment(segment) as OtherSegmentation).value;
      }
      return segmentName;
    }),
  );
}
